import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { NEVER, Observable, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { PopupService } from '../shared/components/popup/popup.service';
import config from '../../config';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SimpleDialog } from '../shared/models/simple-dialog';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private popup: PopupService,
    public router: Router,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const originalUrl = req.url;
    const simpleDialog: SimpleDialog = {
      uiTitle: 'Your session has expired',
      uiMessage:
        "To continue, please click 'OK' to be redirected to the login page.",
      redirect: '/auth/logout',
    };

    // Dont inject the token for the auth enpoints
    if (originalUrl.includes('/auth/') || originalUrl.includes('/login')) {
      return next.handle(req);
    }

    // Convert Promise to Observable and use switchMap to wait for completion
    // catchError is placed BEFORE switchMap to only catch fetchAuthSession errors,
    // not HTTP response errors (which would cause duplicate requests)
    return from(fetchAuthSession()).pipe(
      catchError((error) => {
        console.error('Error fetching auth session:', error);

        // Allow the request to proceed and let the backend handle auth errors
        console.warn('Proceeding with request without authentication');
        return next.handle(req);
      }),
      switchMap((session: any) => {
        const idToken = session.tokens?.idToken;

        const tokenExpirationTime = idToken?.payload.exp;
        const now = Math.floor(Date.now() / 1000);

        // Check for token expiration
        if (tokenExpirationTime && tokenExpirationTime <= now) {
          console.log('Token expired, showing dialog');
          const ref = this.popup.open({
            title: simpleDialog.uiTitle,
            headline: simpleDialog.uiTitle,
            message: simpleDialog.uiMessage,
            icon: { name: 'error', cssClass: 'error-icon' },
            width: '622px',
            hideButtons: true,
            centerContent: true,
            freeze: false,
          });

          // Navigate to logout/redirect when the popup closes (X or any close)
          ref.afterClosed().subscribe(() => {
            this.router.navigate([simpleDialog.redirect ?? '/auth/logout']);
          });

          return NEVER;
        }

        // Add the token to the request
        if (idToken) {
          let modifiedReq = req.clone({
            headers: req.headers.set('id-token', idToken.toString()),
          });

          const lambdaUrl = config.LAMBDA_URL?.replace(/\/+$/, '');
          if (lambdaUrl && originalUrl.startsWith(lambdaUrl)) {
            const accessToken = session.tokens?.accessToken;
            let lambdaHeaders = modifiedReq.headers
              .delete('id-token')
              .set('X-ID-Token', idToken.toString());
            if (accessToken) {
              lambdaHeaders = lambdaHeaders.set(
                'Authorization',
                `Bearer ${accessToken.toString()}`,
              );
            }
            modifiedReq = modifiedReq.clone({ headers: lambdaHeaders });
          }

          return next.handle(modifiedReq);
        } else {
          console.warn('No access token available, redirecting to login');
          this.router.navigate([simpleDialog.redirect ?? '/auth/logout']);
          return NEVER;
        }
      }),
    );
  }
}
